import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { JabraLoader } from '../JabraLoader/JabraLoader';
import { useHeaderNavigation } from '../MegaMenu';

type LanguageItemProps = {
  icon: {
    value: {
      src: string;
    };
  };
  isActive?: boolean;
  externalHost: string;
  locale: string;
  language: string;
  country: string;
  focused: boolean;
};

export const LanguageItem = ({
  locale,
  externalHost,
  icon,
  country,
  language,
  focused,
  isActive,
}: LanguageItemProps) => {
  const router = useRouter();
  const ref = useRef<HTMLAnchorElement>(null);
  const { closeMenu, onLanguageSelect, mediaHost } = useHeaderNavigation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (focused) {
      ref.current?.focus();
    }
  }, [focused]);

  const canConstructLink = locale || externalHost;
  if (!canConstructLink) return null;

  const isInternalRedirect = !!locale;
  externalHost = externalHost.replace(/\/$/, '');
  const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onLanguageSelect?.();
    setIsLoading(true);
    await router.push(router.asPath, router.asPath, { locale });
    setIsLoading(false);
    closeMenu();
  };

  const LanguageItemContent = () => (
    <>
      <span className="mr-2 flex min-h-10 min-w-10 items-center justify-center no-underline">
        <Image
          field={{
            ...icon,
            value: {
              ...icon.value,
              src: icon.value.src ? `${mediaHost}${icon.value.src}` : undefined,
            },
          }}
          width={40}
          height={40}
          alt={`${country} flag`}
        />
      </span>
      <span className="flex flex-col justify-center text-sm no-underline">
        <strong>{country}</strong>
        <span>{language}</span>
      </span>
    </>
  );
  const generalLinkProps = {
    ref,
    className: classNames(
      'flex w-full flex-row items-center rounded-lg px-3 py-2 !no-underline hover:shadow-2xl max-lg:bg-white',
      isActive && 'border border-yellow'
    ),
  };

  return (
    <li>
      {isLoading && <JabraLoader global={true} />}
      {isInternalRedirect ? (
        <Link
          href={router.asPath}
          locale={locale}
          prefetch={false}
          onClick={handleClick}
          {...generalLinkProps}
        >
          <LanguageItemContent />
        </Link>
      ) : (
        <a href={`${externalHost}${router.asPath}`} {...generalLinkProps}>
          <LanguageItemContent />
        </a>
      )}
    </li>
  );
};
